import { Box, Divider, Link, Skeleton, TextField } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { TAG_COLORS } from '../../constants/constants';
import _ from 'lodash';
import { i18n } from '../../i18n/i18n';
import { COLORS } from '@cellebrite/saas-ui-common';
import Popup from '../Popup/Popup';
import { PlusIcon } from '@cellebrite/saas-ui-icons';
import { CustomList } from '../index';
import { ArrowDropDownIcon } from '@mui/x-date-pickers-pro';
import IconButton from '@mui/material/IconButton';
import { GlobalContext } from '../../contexts/globalContext';
import TagRow from './components/TagRow/TagRow';
import { Tag } from '../../models/common-types';
import CheckedTagRow from './components/CheckedTagRow/CheckedTagRow';
import { ReactComponent as CheckedIcon } from '../../../common/images/checked.svg';

interface TagsManagerProps {
    setTags?: any;
    checkedTags: Array<Tag>;
    section: string;
    elementId: string;
}
const TagsManager: React.FC<TagsManagerProps> = ({ setTags, checkedTags, section, elementId }) => {
    const { tagsData } = useContext(GlobalContext);
    const getFirstUnusedTagColor = () => {
        return TAG_COLORS.filter((color) => !tagsData.tags.map((tag) => tag.color).includes(color))[0];
    };
    const [loading, setLoading] = useState(false);
    const [creatingTag, setCreatingTag] = useState(false);
    const [newTagColor, setNewTagColor] = useState(getFirstUnusedTagColor());
    const [newTagName, setNewTagName] = useState('');
    const [currentCheckedTags, setCurrentCheckedTags] = useState(checkedTags);
    const [editTag, setEditTag] = useState<any>();

    useEffect(() => {
        if (!_.isEqual(checkedTags, currentCheckedTags)) {
            setCurrentCheckedTags(checkedTags);
            setEditTag(null);
        }
    }, [checkedTags]);
    useEffect(() => {
        setNewTagColor(getFirstUnusedTagColor());
    }, [tagsData.tags]);
    useEffect(() => {
        if (editTag) {
            setNewTagColor(editTag.color);
            setNewTagName(editTag.name);
        }
    }, [editTag]);
    const getErrorText = (input) => {
        let errorText = '';
        input = input.trim();
        if (input) {
            if (tagsData.tags.map((tag) => tag.name.toLowerCase()).includes(input.toLowerCase())) {
                errorText = i18n.translate('name similarity');
            }
        }
        return errorText;
    };
    const [inputErrorText, setInputErrorText] = useState(getErrorText(newTagName));
    const getSkeleton = () => {
        const rowSkeleton = (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Skeleton sx={{ ml: 1, p: 0.5, width: 16, height: 16, backgroundColor: COLORS.MUTED_HOVER }} />
                <Skeleton
                    sx={{
                        ml: 1,
                        mr: 1,
                        width: 220,
                        height: 14,
                        backgroundColor: COLORS.MUTED_HOVER,
                        borderRadius: '8px',
                    }}
                />
            </Box>
        );
        const result = [];
        for (let i = 0; i < 7; i++) {
            result.push(rowSkeleton);
        }
        return <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 1 }}>{result}</Box>;
    };
    const handleUpdateTags = () => {
        setLoading(true);
        if (editTag) {
            tagsData.editTag({
                data: {
                    id: editTag.id,
                    name: newTagName,
                    color: newTagColor,
                },
                successCallback: () => {
                    setCreatingTag(false);
                    setNewTagColor(getFirstUnusedTagColor());
                    setNewTagName('');
                    setEditTag(null);
                    setLoading(false);
                },
                errorCallback: () => {
                    setLoading(false);
                },
            });
        } else {
            tagsData.createTag({
                data: { name: newTagName, color: newTagColor },
                successCallback: () => {
                    setCreatingTag(false);
                    setNewTagColor(getFirstUnusedTagColor());
                    setNewTagName('');
                    setLoading(false);
                },
                errorCallback: () => {
                    setLoading(false);
                },
            });
        }
    };
    const displayDivider =
        !!currentCheckedTags?.filter((tag) => tag.id !== editTag?.id).length &&
        !!tagsData.tags.filter((tag) => !currentCheckedTags?.map((tag) => tag.id).includes(tag.id)).length;
    const invalidUpdate = editTag
        ? !newTagName || Boolean(inputErrorText) || editTag.name === newTagName
        : !newTagName || Boolean(inputErrorText);
    return (
        <Box sx={{ minHeight: 176, width: 288, px: 1, py: 2, backgroundColor: COLORS.LIGHT }}>
            {loading ? (
                getSkeleton()
            ) : (
                <Box>
                    <Box sx={{ maxHeight: displayDivider ? 221 : 204, overflowY: 'auto', overflowX: 'hidden' }}>
                        {currentCheckedTags
                            ?.filter((tag) => tag.id !== editTag?.id)
                            .map((tag, index) => {
                                return (
                                    <CheckedTagRow
                                        key={index}
                                        tag={tag}
                                        section={section}
                                        elementId={elementId}
                                        setLoading={setLoading}
                                        setEditTag={setEditTag}
                                        setTags={setTags}
                                    />
                                );
                            })}
                        {displayDivider && (
                            <Divider
                                sx={{
                                    mt: 1,
                                    mb: 1,
                                    backgroundColor: COLORS.MUTED_SHADY,
                                    position: 'relative',
                                    width: 'calc(100% + 16px)',
                                    left: '-8px',
                                }}
                            />
                        )}
                        {tagsData.tags
                            .filter(
                                (tag) =>
                                    !currentCheckedTags?.map((tag) => tag.id).includes(tag.id) &&
                                    tag.id !== editTag?.id,
                            )
                            .map((tag, index) => {
                                return (
                                    <TagRow
                                        key={index}
                                        tag={tag}
                                        section={section}
                                        elementId={elementId}
                                        setLoading={setLoading}
                                        setEditTag={setEditTag}
                                        setTags={setTags}
                                    />
                                );
                            })}
                    </Box>
                    {(creatingTag || editTag) && (
                        <Box sx={{ height: 68, pt: 1.5 }}>
                            <Popup
                                popperStyles={{ zIndex: 3 }}
                                actionElement={(handleOpen, anchorRef, open) => {
                                    return (
                                        <Box ref={anchorRef} sx={{ display: 'flex', alignItems: 'center', height: 40 }}>
                                            <Box
                                                sx={{
                                                    'ml': 0.6,
                                                    'width': 24,
                                                    'height': 24,
                                                    'alignItems': 'center',
                                                    'justifyContent': 'center',
                                                    'display': 'flex',
                                                    'backgroundColor': open ? COLORS.BRAND_LIGHTEST : null,
                                                    '&:hover': { backgroundColor: COLORS.BRAND_LIGHTEST },
                                                    'borderRadius': '8px',
                                                }}
                                                onClick={handleOpen}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 12,
                                                        height: 12,
                                                        backgroundColor: newTagColor,
                                                        borderRadius: 2,
                                                    }}
                                                ></Box>
                                            </Box>
                                            {open ? (
                                                <ArrowDropDownIcon
                                                    onClick={handleOpen}
                                                    style={{
                                                        color: COLORS.MUTED_DARK,
                                                        transform: 'rotate(180deg) scaleX(-1)',
                                                    }}
                                                />
                                            ) : (
                                                <ArrowDropDownIcon
                                                    onClick={handleOpen}
                                                    style={{ color: COLORS.MUTED_DARK }}
                                                />
                                            )}
                                            <TextField
                                                sx={{ mx: 1, alignSelf: 'baseline' }}
                                                label={i18n.translate('Tag name')}
                                                value={newTagName}
                                                error={Boolean(inputErrorText)}
                                                helperText={inputErrorText}
                                                onChange={(e) => {
                                                    setNewTagName(e.target.value);
                                                    setInputErrorText(getErrorText(e.target.value));
                                                }}
                                                onKeyUp={(event) => {
                                                    if (
                                                        event.key === 'Enter' &&
                                                        !(!newTagName || Boolean(inputErrorText))
                                                    ) {
                                                        handleUpdateTags();
                                                    }
                                                }}
                                            />
                                            <IconButton
                                                disabled={invalidUpdate}
                                                disableRipple
                                                sx={{
                                                    'color': COLORS.MUTED_DARK,
                                                    '&:hover': { backgroundColor: COLORS.MUTED_HOVER },
                                                }}
                                                onClick={handleUpdateTags}
                                            >
                                                {
                                                    <IconButton
                                                        sx={{
                                                            width: 24,
                                                            height: 24,
                                                            backgroundColor: COLORS.MUTED_HOVER,
                                                        }}
                                                    >
                                                        {editTag ? (
                                                            <CheckedIcon
                                                                fill={invalidUpdate ? COLORS.MUTED_SHADY : COLORS.BRAND}
                                                            />
                                                        ) : (
                                                            <PlusIcon
                                                                fill={invalidUpdate ? COLORS.MUTED_SHADY : COLORS.BRAND}
                                                            />
                                                        )}
                                                    </IconButton>
                                                }
                                            </IconButton>
                                        </Box>
                                    );
                                }}
                                contentElement={(handleClose) => (
                                    <Box sx={{ p: 1.5, maxWidth: 144, backgroundColor: COLORS.LIGHT }}>
                                        <CustomList
                                            gap={{ width: 0, height: 0 }}
                                            list={TAG_COLORS.filter(
                                                (color) =>
                                                    color !== newTagColor &&
                                                    !tagsData.tags.map((tag) => tag.color).includes(color),
                                            ).map((color) => {
                                                return (
                                                    <Box
                                                        sx={{
                                                            'width': 24,
                                                            'height': 24,
                                                            'alignItems': 'center',
                                                            'justifyContent': 'center',
                                                            'display': 'flex',
                                                            '&:hover': { backgroundColor: COLORS.MUTED_HOVER },
                                                        }}
                                                        onClick={() => setNewTagColor(color)}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 12,
                                                                height: 12,
                                                                backgroundColor: color,
                                                                borderRadius: 2,
                                                            }}
                                                        ></Box>
                                                    </Box>
                                                );
                                            })}
                                        />
                                    </Box>
                                )}
                            />
                        </Box>
                    )}
                    <Box sx={{ py: 1, px: 2 }}>
                        <Link
                            sx={{ color: creatingTag || editTag ? COLORS.MUTED_DARK : null }}
                            onClick={() => (creatingTag || editTag ? null : setCreatingTag(true))}
                        >
                            {i18n.translate('Create new tag')}
                        </Link>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default TagsManager;
